import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Input, Checkbox, Button, Modal  } from 'antd';
import { api_device } from '../../../api/config';
import { device, initDevice } from '../../../model/config-general';
import { json } from 'stream/consumers';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { isEmpty } from '../../../utils/util';
import { deleteFailureMessage, writeFailureMessage } from '../../../utils/write-result';
import { userSelector } from '../../../store/user/slice';
import { mayManageDeviceSessions } from '../../../auth/device-session-permission';

const ConfigDevice: React.FC = (prop) => {

    const [data, setData] = useState<any>([]);
    const [device, setDevice] = useState<device[]>([]);

    // Display only. The Backend enforces the same rule -- Admin privilege --
    // and answers 403 to anyone else. `privilege` here is the value
    // `SESSION_ME` resolved from the session, which is the same value the
    // Backend decides with, so there is no second list to drift.
    const user = useSelector(userSelector);
    const canRevokeSessions = mayManageDeviceSessions(user?.privilege);

    const init = async () => {
        let getDevice = await api_device('LIST', {});
        if (getDevice.length > 0) {
            setDevice(getDevice);
        } 
    }

    // `content` is a ReactNode, not a string: the revoke confirmation has
    // to be two paragraphs, and a newline inside antd's content renders as
    // a space. Every existing caller still passes a plain string.
    const getConfigModal = (titile: string, content: React.ReactNode) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        }
    }

    useEffect(() => {
        setDevice(data);
    }, [data]);

    const appendDevice = () => {
        let item = JSON.parse(JSON.stringify(device));
        let object = JSON.parse(JSON.stringify(initDevice));
        if (item.length > 0) {
            let key = item[item.length - 1]['key']
            if (!isEmpty(key)) {
                key = key + 1;
                object.key = key;
            }
        }
        item.push(object);
        setData(item);
    }

    const onDeviceChange = (e: any, index: number) => {
        let key: string = String(e.target.name);
        let value: any = (
            e.target.type === 'checkbox'
                ? (e.target.checked ? 1 : 0)
                : e.target.type === 'radio' ? e.target.value
                : typeof(e.target.value) === 'number' ? Number(e.target.value)
                : String(e.target.value)
        );
        let object: any = device;
        object[index][key] = value;

        setData([...object]);
    }

    // Where to find the machine's IP, per platform.
    //
    // A browser cannot read its own LAN address -- that is a deliberate
    // privacy restriction, not a gap to work around -- and the address the
    // Backend sees is the public/NAT one, which is not what this field
    // means. So the honest design is: the operator looks it up, and this
    // tells them how.
    const showIpHelp = () => {
        Modal.info({
            title: 'วิธีค้นหา IP Address ของเครื่อง',
            okText: 'เข้าใจแล้ว',
            centered: true,
            width: 520,
            content: (
                <div className='ip-help-content'>
                    <p><b>Windows</b><br />
                        กด <code>Win + R</code> → พิมพ์ <code>cmd</code> → พิมพ์ <code>ipconfig</code><br />
                        ดูที่ <b>IPv4 Address</b> ของการ์ด Wi-Fi หรือ Ethernet ที่กำลังเชื่อมต่ออยู่</p>
                    <p><b>macOS</b><br />
                        System Settings → Network → เลือก Wi-Fi หรือ Ethernet ที่เชื่อมต่ออยู่ → Details → TCP/IP → <b>IPv4 Address</b></p>
                    <p><b>Android</b><br />
                        การตั้งค่า → Wi-Fi → แตะเครือข่ายที่เชื่อมต่ออยู่ → ดู <b>IP address</b></p>
                    <p><b>iPhone / iPad</b><br />
                        การตั้งค่า → Wi-Fi → แตะ <b>ⓘ</b> ข้างเครือข่ายที่เชื่อมต่ออยู่ → ดู <b>IP Address</b></p>
                    <p className='ip-help-warning'>
                        IP ภายในเครือข่ายอาจเปลี่ยนได้เมื่อ Router แจก IP ใหม่
                        ระบบจึงใช้ Device UUID เป็นตัวตนหลักของอุปกรณ์ ไม่ใช้ IP เป็นตัวตนหลัก
                    </p>
                </div>
            ),
        });
    };

    // "เตะออก" -- sign a registered till out everywhere.
    //
    // This is NOT delete. The device row stays, keeps its identity, and can
    // sign in again; what ends is every live session on that machine. The
    // confirmation says so in those words, because the two buttons now sit
    // next to each other and "ลบ" and "เตะออก" are not recoverable in the
    // same way.
    //
    // Only `key-id` goes to the Backend. The device's real identity
    // (`device_uuid`) is read there from the row -- this screen never holds
    // it and must not, or naming it would become a way to kick a machine
    // nobody had been shown.
    const onRevokeSessions = (body: any) => {
        const deviceName = isEmpty(body['name']) ? 'อุปกรณ์นี้' : body['name'];

        Modal.confirm({
            ...getConfigModal(
                'ยืนยันการเตะออกจากระบบ',
                <div className='revoke-confirm-content'>
                    <p>ต้องการให้ <b>{deviceName}</b> ออกจากระบบทุกการเข้าใช้งานที่ยังค้างอยู่ใช่หรือไม่?</p>
                    <p>ผู้ที่กำลังใช้งานอยู่บนเครื่องนี้จะต้องเข้าสู่ระบบใหม่
                        อุปกรณ์จะยังคงอยู่ในรายการและยังลงทะเบียนไว้เหมือนเดิม
                        <b> ไม่ใช่การลบอุปกรณ์</b></p>
                </div>
            ),
            okText: 'เตะออก',
            okButtonProps: { danger: true },
            async onOk() {
                let res: any;
                try {
                    res = await api_device('REVOKE_SESSIONS', {}, body['id']);
                } catch (err) {
                    // 403 (ไม่มีสิทธิ์), 404 (อุปกรณ์ถูกลบไปแล้ว) และ 409
                    // (ยังไม่ผูกเบราว์เซอร์ / เป็นเครื่องที่กำลังใช้อยู่) ล้วน
                    // มาพร้อมประโยคที่ Backend เขียนไว้ให้ผู้ใช้อ่าน -- แสดง
                    // ประโยคนั้น ไม่ใช่ข้อความรวมที่บอกอะไรไม่ได้
                    Modal.error({
                        ...getConfigModal('ผิดพลาด',
                            writeFailureMessage(err, 'เตะออกจากระบบไม่สำเร็จ')),
                    });
                    return;
                }

                // The wrapper answers `[]` for anything that was not a 200,
                // so a count is the only thing that proves the Backend did
                // this. A missing count is not reported as a success.
                const revoked = Number(res?.revoked_count);
                if (!Number.isFinite(revoked)) {
                    Modal.error({
                        ...getConfigModal('ผิดพลาด', 'เตะออกจากระบบไม่สำเร็จ'),
                    });
                    return;
                }

                Modal.success({
                    ...getConfigModal('สำเร็จ', revoked > 0
                        ? `เตะออกจากระบบแล้ว ${revoked} การเข้าใช้งาน`
                        : 'อุปกรณ์นี้ไม่มีการเข้าใช้งานที่ค้างอยู่ จึงไม่มีอะไรต้องเตะออก'),
                });
            },
        });
    }

    const onSaveDevice = async (operation: string, body: any, index: number) => {
        let res = 0;
        let key_id = undefined;

        if (operation == 'DELETE') {
            // `key-id` is the record id, and the Backend deletes by
            // `WHERE id = %s`. This sent `body['name']`, so every delete of
            // a saved device was refused by `validate_key_id` -- which
            // requires a whole number -- before it ever reached the
            // service. `api_device` has no catch either, so the rejected
            // promise showed the operator nothing at all: no success, no
            // error, the row simply stayed.
            key_id = body['id'];
            if (body['id'] == 0) {
                let item = JSON.parse(JSON.stringify(device));
                item.splice(index, 1);
                setData(item);
                res = 1;
            } else {
                try {
                    res = await api_device(operation, body, key_id);
                } catch (err) {
                    // Backend answers a device that is already gone with a
                    // 404 and a sentence written for the user. The generic
                    // "จัดการมูลไม่สำเร็จ" below cannot tell that apart from
                    // a network failure, so a real 4xx says what happened.
                    Modal.error({
                        ...getConfigModal('ผิดพลาด', deleteFailureMessage(err, 'ลบข้อมูลไม่สำเร็จ')),
                    });
                    return;
                }
            }
        } else {
            if (!isEmpty(body['name']) && !isEmpty(body['device'])) {
                // `body['id'] = body['name']` used to sit here. `device.id`
                // is an int column, and Staging's sql_mode is not strict, so
                // MariaDB turned the name into 0 and wrote a row nothing
                // could address -- which is why this screen has never
                // successfully saved anything. The id is now the database's
                // to hand out: a row the operator just added still carries
                // the component's own `id: 0`, which Backend reads as new,
                // and a row that came from LIST carries its real id, which
                // Backend updates in place.
                try {
                    res = await api_device(operation, body, key_id);
                } catch (err) {
                    // Saving can now fail for a reason worth reading: the
                    // device was removed by someone else (404), or the id
                    // was not an id (400). Without this the rejected promise
                    // showed nothing at all.
                    Modal.error({
                        ...getConfigModal('ผิดพลาด', writeFailureMessage(err, 'จัดการมูลไม่สำเร็จ')),
                    });
                    return;
                }
            } else {
                Modal.warning({
                    ...getConfigModal('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน')
                });
            }
        }

        if (res === 1) {
            Modal.success({
                ...getConfigModal('สำเร็จ', 'จัดการมูลสำเร็จแล้ว'),
                ...{ onOk() { init(); }}
            });
        } else {
            Modal.error({
                ...getConfigModal('ผิดพลาด', 'จัดการมูลไม่สำเร็จ'),
            });
        }
    }

    useEffect(() => {
        init();
    }, []);

    return (
        <>
            <div className='configDeviceContainer'>
                <div className='configBox' style={{marginTop: '20px'}}>
                    <label className='title'>ตั้งค่าอุปกรณ์</label>
                    <Button className='btn-common tool-title' onClick={appendDevice}>+</Button>
                    {
                        device.map((obj, inx) => {
                            return <div className='configDeviceContainerDetail' key={obj.key}>
                                <div className='configDeviceContainerInput'>
                                    <div>
                                        <label>ชื่ออุปกรณ์</label>
                                        <Input value={obj.name} name="name" placeholder="เช่น เครื่องแคชเชียร์ 1" onChange={(e: any) => {onDeviceChange(e, inx)}} />
                                    </div>
                                    <div>
                                        <label>
                                            IP Address
                                            {/* The IP is a convenience, not the device's identity -- it
                                                changes whenever the router hands out a new lease. The
                                                help explains where to find it, and says so plainly. */}
                                            <button
                                                type="button"
                                                className="btn-ip-help"
                                                aria-label="วิธีค้นหา IP Address ของเครื่อง"
                                                title="วิธีค้นหา IP Address ของเครื่อง"
                                                onClick={showIpHelp}
                                            >?</button>
                                        </label>
                                        <Input value={obj.device} name="device" placeholder="เช่น 192.168.1.50" onChange={(e: any) => {onDeviceChange(e, inx)}} />
                                    </div>
                                </div>
                                <div className='configDeviceContainerTool'>
                                    <Button className='btn-common' onClick={() => {onSaveDevice('INSERT', obj, inx)}}><FontAwesomeIcon icon="floppy-disk" /></Button>
                                    <Button className='btn-common' onClick={() => {onSaveDevice('DELETE', obj, inx)}}><FontAwesomeIcon icon="xmark" /></Button> 
                                    {
                                        // Only for a row the database has an id for: an
                                        // unsaved row (`id: 0`) has no sessions to end,
                                        // and sending 0 would just be a 400.
                                        canRevokeSessions && Number(obj.id) > 0 && (
                                            <Button
                                                className='btn-common btn-revoke-sessions'
                                                onClick={() => {onRevokeSessions(obj)}}
                                            >เตะออก</Button>
                                        )
                                    }
                                </div>
                            </div>
                        })
                    }
                </div>
            </div>
        </>
    )
}

export default ConfigDevice;